<template>
    <!-- Structured MCP `fetch` — the sub-second counterpart to `ask` (PLT-1919). Whether a
         path is readable is decided by the tenant's Work IQ MCP policy, so a denial is
         called out as a governance decision rather than shown as a generic error. -->
    <div class="flex flex-col gap-6">
        <p class="body-small text-ink-muted -mt-2">
            Read Microsoft 365 entities directly by resource path, with no LLM in the loop. Runs on the same
            <span class="font-mono">WorkIQAgent.Ask</span> scope as the Prompt tab — no extra consent — but each path is allowed or denied by your tenant's Work IQ MCP policy.
        </p>

        <BaseCard v-if="!connected" class="body-small text-ink-muted"> Connect Work IQ on the Prompt tab to run structured lookups. </BaseCard>

        <template v-else>
            <!-- What the MCP surface demands and exposes. -->
            <BaseCard class="flex flex-col gap-2">
                <div class="flex items-center justify-between gap-3">
                    <div class="body-bold text-ink">MCP surface</div>
                    <BaseButton theme="tertiary" size="sm" :disabled="infoLoading" @click="loadInfo">
                        {{ infoLoading ? "Checking…" : "Check surface" }}
                    </BaseButton>
                </div>

                <BaseNotification v-if="infoError" theme="error" hide-dismiss>
                    <template #content
                        ><p class="body-small whitespace-pre-wrap break-words">{{ infoError }}</p></template
                    >
                </BaseNotification>

                <template v-else-if="info">
                    <!-- Stacks below sm: a fixed label column plus break-all on long scope and
                         tool strings degrades to one character per line in a narrow column. -->
                    <dl class="flex flex-col gap-2 body-small text-ink-muted">
                        <div class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-ink-muted sm:w-28 sm:flex-shrink-0">Endpoint</dt>
                            <dd class="font-mono break-words">{{ info.resource }}</dd>
                        </div>
                        <div class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-ink-muted sm:w-28 sm:flex-shrink-0">Scopes</dt>
                            <dd class="font-mono break-words">{{ info.scopes_supported.join(", ") || "—" }}</dd>
                        </div>
                        <div v-if="info.server" class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-ink-muted sm:w-28 sm:flex-shrink-0">Server</dt>
                            <dd>{{ info.server }}</dd>
                        </div>
                        <div v-if="info.tools.length" class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-ink-muted sm:w-28 sm:flex-shrink-0">Tools ({{ info.tools.length }})</dt>
                            <dd class="font-mono break-words">{{ info.tools.join(", ") }}</dd>
                        </div>
                    </dl>
                </template>

                <div v-else class="body-small text-ink-muted">Check the surface to see the required scope and available tools.</div>
            </BaseCard>

            <!-- Path input + presets. -->
            <BaseCard class="flex flex-col gap-3">
                <div class="body-bold text-ink">Fetch a resource path</div>

                <div class="flex flex-wrap gap-2">
                    <BaseButton
                        v-for="preset in PRESETS"
                        :key="preset.label"
                        theme="neutral"
                        size="sm"
                        :data-testid="`workiq-fetch-preset-${preset.label}`"
                        @click="path = preset.path()">
                        {{ preset.label }}
                    </BaseButton>
                </div>

                <label for="workiq-fetch-path" class="flex flex-col gap-1">
                    <span class="sr-only">Resource path</span>
                    <BaseTextarea
                        id="workiq-fetch-path"
                        v-model:input="path"
                        disable-autosize
                        show-resize-handle
                        rows="3"
                        spellcheck="false"
                        data-testid="workiq-fetch-path"
                        class="workiq-field font-mono"
                        placeholder="/me/messages?$top=5&$select=subject,from,body" />
                </label>

                <div class="flex items-center justify-between gap-3">
                    <span class="text-xs text-ink-muted">One path per line. Read-only — mutations are blocked by tenant policy by default.</span>
                    <BaseButton theme="primary" size="sm" :disabled="loading || !paths.length" @click="runFetch">
                        {{ loading ? "Fetching…" : "Fetch" }}
                    </BaseButton>
                </div>
            </BaseCard>

            <!-- Result. -->
            <BaseNotification v-if="policyDenied" theme="warning" hide-dismiss>
                <template #content
                    ><div class="flex min-w-0 flex-col gap-2">
                        <div class="body-small-semibold">Blocked by tenant policy</div>
                        <p class="body-small">
                            Your tenant's Work IQ MCP policy doesn't allow this path. That's an admin-center setting (Agents → Tools → Work IQ MCP → Policy), not a consent or token
                            problem — reconnecting won't change it, and changes can take up to 24h to apply.
                        </p>
                        <pre class="text-xs whitespace-pre-wrap break-words">{{ error }}</pre>
                    </div></template
                >
            </BaseNotification>

            <BaseNotification v-else-if="error" theme="error" hide-dismiss data-testid="workiq-fetch-error">
                <template #content
                    ><p class="body-small whitespace-pre-wrap break-words">{{ error }}</p></template
                >
            </BaseNotification>

            <BaseCard v-if="results.length" class="flex flex-col gap-3">
                <div class="flex items-center justify-between gap-3">
                    <div class="body-bold text-ink">Result</div>
                    <span class="text-xs text-ink-muted" data-testid="workiq-fetch-elapsed">{{ elapsedMs }} ms round-trip</span>
                </div>

                <div v-for="(result, index) in results" :key="index" class="flex flex-col gap-2 border-t border-border pt-2 first:border-t-0 first:pt-0">
                    <div class="flex items-center flex-wrap gap-2">
                        <span class="font-mono text-xs text-ink-muted break-all">{{ entityUrls[index] }}</span>
                        <StatusChip :state="result.status_code === 200 ? 'done' : 'neutral'" size="2xs" :class="{ 'text-ink-muted': result.status_code !== 200 }">
                            {{ result.status_code }}
                        </StatusChip>
                        <span class="text-xs text-ink-muted">{{ result.row_count }} {{ result.row_count === 1 ? "entity" : "entities" }}</span>
                        <BaseChip v-if="result.body_chars" size="2xs" class="text-ink-muted"> Full bodies · {{ formatNumber(result.body_chars) }} chars </BaseChip>
                    </div>
                    <BaseNotification v-if="result.error" theme="error" hide-dismiss>
                        <template #content
                            ><p class="body-small whitespace-pre-wrap break-words">{{ result.error }}</p></template
                        >
                    </BaseNotification>
                    <pre class="text-xs text-ink bg-surface-sunken border border-border rounded-lg p-3 overflow-auto max-h-96">{{ pretty(result.data) }}</pre>
                </div>
            </BaseCard>
        </template>
    </div>
</template>

<script setup lang="ts">
import "~vue/components/workiq/playground.css";
import { getCookie } from "/js/utils";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed, ref } from "vue";

interface McpInfo {
    resource: string;
    scopes_supported: string[];
    tools: string[];
    server: string;
    error?: string;
}

interface FetchResultRow {
    status_code: number | null;
    error: string | null;
    row_count: number;
    body_chars: number;
    data: unknown;
}

const props = defineProps<{
    connected: boolean;
    mcpInfoUrl: string;
    fetchUrl: string;
}>();

// `body` is in $select deliberately: the open question this tab answers is whether the
// structured surface returns real message content or only metadata.
const MESSAGE_FIELDS = "subject,from,receivedDateTime,isRead,bodyPreview,body";

function isoDaysFromNow(days: number): string {
    return new Date(Date.now() + days * 86_400_000).toISOString();
}

const PRESETS = [
    { label: "recent mail", path: () => `/me/messages?$top=5&$orderby=receivedDateTime desc&$select=${MESSAGE_FIELDS}` },
    { label: "unread mail", path: () => `/me/messages?$top=5&$filter=isRead eq false&$select=${MESSAGE_FIELDS}` },
    { label: "calendar ±14d", path: () => `/me/calendarView?startDateTime=${isoDaysFromNow(-14)}&endDateTime=${isoDaysFromNow(14)}&$top=25` },
    { label: "chats", path: () => "/me/chats?$top=10" },
    { label: "people", path: () => "/me/people?$top=10" },
    { label: "me", path: () => "/me" },
] as const;

const path = ref(PRESETS[0].path());
const info = ref<McpInfo | null>(null);
const infoError = ref("");
const infoLoading = ref(false);

const entityUrls = ref<string[]>([]);
const results = ref<FetchResultRow[]>([]);
const elapsedMs = ref(0);
const error = ref("");
const policyDenied = ref(false);
const loading = ref(false);

const connected = computed(() => props.connected);
const paths = computed(() =>
    path.value
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
);

function pretty(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

async function loadInfo() {
    infoLoading.value = true;
    infoError.value = "";
    try {
        const { data, response } = await useFetch(props.mcpInfoUrl).get().json();
        if (response.value?.ok) {
            info.value = data.value as McpInfo;
            infoError.value = info.value?.error ?? "";
        } else {
            info.value = null;
            infoError.value = data.value?.error || "Failed to read the MCP surface.";
        }
    } finally {
        infoLoading.value = false;
    }
}

async function runFetch() {
    loading.value = true;
    error.value = "";
    policyDenied.value = false;
    results.value = [];
    try {
        const { data, response } = await useFetch(props.fetchUrl, {
            headers: { "Content-Type": "application/json", "X-CSRFToken": getCookie("csrftoken") ?? "" },
        })
            .post({ entity_urls: paths.value })
            .json();

        if (response.value?.ok) {
            entityUrls.value = data.value?.entity_urls ?? paths.value;
            results.value = data.value?.results ?? [];
            elapsedMs.value = data.value?.elapsed_ms ?? 0;
        } else {
            policyDenied.value = Boolean(data.value?.policy_denied);
            error.value = data.value?.error || "Fetch failed.";
        }
    } finally {
        loading.value = false;
    }
}
</script>
